import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SamsaraLogo from '../assets/Samsara_Logo.svg';
import AnimatedBackground from './AnimatedBackground';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';

interface LayoutProps {
    children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
    const [isDropdownHovered, setDropdownHovered] = useState(false);
    const hideDropdownTimeout = useRef<number | null>(null);
    const location = useLocation();

    const isActive = (path: string) => location.pathname === path;

    // Colors from AnimatedBackground.tsx - excluding pink ones
    const COLORS = [
        ['#3F013D', '#ae4094'], // Deep purple to accent purple (your brand colors)
        ['#1a0033', '#6b0f5e'], // Dark purple to deep magenta
        ['#2d1b69', '#8b5cf6'], // Deep blue-purple to violet
        ['#0f172a', '#475569'], // Dark slate to medium slate
        ['#581c87', '#c084fc'], // Deep purple to light purple
        ['#1e1b4b', '#6366f1'], // Dark indigo to indigo
        ['#374151', '#9ca3af'], // Dark gray to light gray
        ['#4c1d95', '#a855f7'], // Deep violet to purple
        ['#5b1065', '#d946ef'], // Rich purple to bright magenta
        ['#1e3a8a', '#3b82f6'], // Deep blue to bright blue
        ['#0c4a6e', '#0ea5e9'], // Dark blue to sky blue
        ['#164e63', '#06b6d4'], // Dark cyan to cyan
        ['#0f172a', '#1e40af'], // Very dark blue to blue
        ['#312e81', '#6366f1'], // Dark indigo to indigo
    ];

    // Filter out pink colors and select good ones for the glow
    const GLOW_COLORS = [
        COLORS[0][0], // Deep purple
        COLORS[2][1], // Violet
        COLORS[5][1], // Indigo
        COLORS[9][1], // Bright blue
        COLORS[11][1], // Cyan
        COLORS[3][1], // Medium slate
    ];

    const navigationItems = [
        { path: '/about', label: 'About' },
        { path: '/team', label: 'Team' },
        { path: '/thesis', label: 'Thesis' },
        { path: '/augury', label: 'Augury' },
        { path: '/careers', label: 'Careers' }
    ];

    const handleDropdownEnter = () => {
        if (hideDropdownTimeout.current) {
            clearTimeout(hideDropdownTimeout.current);
        }
        setDropdownHovered(true);
    };

    const handleDropdownLeave = () => {
        hideDropdownTimeout.current = window.setTimeout(() => {
            setDropdownHovered(false);
        }, 500);
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            setCursorPosition({ x: e.clientX, y: e.clientY });
        };

        document.addEventListener('mousemove', handleMouseMove);
        return () => document.removeEventListener('mousemove', handleMouseMove);
    }, []);

    return (
        <div className="min-h-screen bg-black flex flex-col relative">
            <AnimatedBackground />

            {/* Transparent blur overlay */}
            <div
                className="fixed inset-0 bg-black bg-opacity-5 pointer-events-none"
                style={{
                    zIndex: 1,
                    backdropFilter: 'blur(27px)',
                    WebkitBackdropFilter: 'blur(27px)' // Safari support
                }}
            />
            <header className="py-6 px-4 md:px-12 flex justify-between items-center relative z-30">
                <Link to="/" className="text-2xl font-bold tracking-tighter flex items-center">
                    <img src={SamsaraLogo} alt="Samsara" className="h-8 w-8 mr-2" />
                    SAMSARA
                </Link>

                {/* Mobile menu button */}
                <button
                    className="md:hidden z-50"
                    onClick={() => setMenuOpen(!menuOpen)}
                >
                    <div className={`w-6 h-0.5 bg-white mb-1.5 transition-all ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}></div>
                    <div className={`w-6 h-0.5 bg-white mb-1.5 transition-all ${menuOpen ? 'opacity-0' : 'opacity-100'}`}></div>
                    <div className={`w-6 h-0.5 bg-white transition-all ${menuOpen ? '-rotate-45' : ''}`}></div>
                </button>

                {/* Desktop dropdown navigation */}
                <div
                    className="hidden md:block relative dropdown-container"
                    onMouseEnter={handleDropdownEnter}
                    onMouseLeave={handleDropdownLeave}
                >
                    <button className="flex items-center space-x-2 text-white hover:text-accent-light transition-colors px-4 py-2 rounded-lg bg-gray-900 bg-opacity-50 backdrop-blur-sm border border-gray-700 hover:border-accent-light">
                        <div className="w-5 h-5 flex flex-col justify-center items-center space-y-1 group">
                            <div className="w-4 h-0.5 bg-current transition-all duration-200 group-hover:rotate-45 group-hover:translate-y-1.5"></div>
                            <div className="w-4 h-0.5 bg-current transition-all duration-200 group-hover:opacity-0"></div>
                            <div className="w-4 h-0.5 bg-current transition-all duration-200 group-hover:-rotate-45 group-hover:-translate-y-1.5"></div>
                        </div>
                    </button>

                    {/* Dropdown menu */}
                    <div
                        className={`dropdown-menu absolute right-0 mt-2 w-48 bg-gray-900 bg-opacity-95 backdrop-blur-lg rounded-lg shadow-xl border border-gray-700 overflow-hidden transition-all duration-300 ease-in-out ${isDropdownHovered ? 'opacity-100 translate-y-0 visible' : 'opacity-0 -translate-y-2 invisible'}`}
                    >
                        <div className="py-2">
                            {navigationItems.map((item, index) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`nav-link block px-4 py-3 text-sm font-medium transition-all duration-200 border-l-2 border-transparent group ${isActive(item.path)
                                        ? 'text-accent-light bg-accent bg-opacity-10 border-l-accent-light'
                                        : 'text-gray-300 hover:bg-accent hover:bg-opacity-20 hover:border-accent-light'
                                        }`}
                                    style={{
                                        animationDelay: `${index * 50}ms`
                                    }}
                                >
                                    <span
                                        className={`transition-all duration-200 ${isActive(item.path)
                                            ? 'text-accent-light drop-shadow-[0_0_8px_rgba(174,64,148,0.8)]'
                                            : 'group-hover:text-accent-light group-hover:drop-shadow-[0_0_8px_rgba(174,64,148,0.6)]'
                                            }`}
                                    >
                                        {item.label}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Custom cursor container - controlled entirely by CSS */}
                    <div
                        className={`custom-cursor-container fixed pointer-events-none transition-opacity duration-150 ease-in-out ${isDropdownHovered ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                        style={{
                            left: cursorPosition.x,
                            top: cursorPosition.y,
                            zIndex: 9999,
                            transform: 'translate(-50%, -50%)'
                        }}
                    >
                        <div className="custom-cursor">
                            {/* Animated glow ring - counter-clockwise rotation */}
                            <div
                                className="custom-cursor-glow spin-counter-clockwise"
                                style={{
                                    background: `conic-gradient(from 0deg, ${GLOW_COLORS[0]}CC, ${GLOW_COLORS[1]}99, ${GLOW_COLORS[2]}CC, ${GLOW_COLORS[3]}99, ${GLOW_COLORS[4]}CC, ${GLOW_COLORS[5]}99, ${GLOW_COLORS[0]}CC)`,
                                    mask: 'radial-gradient(circle at center, transparent 10px, black 12px)',
                                    WebkitMask: 'radial-gradient(circle at center, transparent 10px, black 12px)'
                                }}
                            />

                            {/* Glass circle background */}
                            <div
                                className="custom-cursor-glass"
                                style={{
                                    boxShadow: `0 0 20px ${COLORS[0][1]}4D, inset 0 0 20px rgba(255, 255, 255, 0.1)`
                                }}
                            />

                            {/* Arrow icon */}
                            <div className="custom-cursor-arrow">
                                <svg
                                    className="w-3 h-3"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile menu */}
            <div
                className={`fixed inset-0 bg-black z-40 flex flex-col items-center justify-center transform transition-transform duration-300 ease-in-out ${menuOpen ? 'translate-x-0' : 'translate-x-full'
                    } md:hidden`}
            >
                <nav className="flex flex-col space-y-8 text-2xl text-center">
                    {navigationItems.map((item) => (
                    <Link
                            key={item.path}
                            to={item.path}
                            className={`hover:text-accent transition-colors ${isActive(item.path) ? 'text-accent-light' : ''}`}
                        onClick={() => setMenuOpen(false)}
                    >
                            {item.label}
                    </Link>
                    ))}
                </nav>
            </div>

            <main className="flex-1 relative z-10">
                {children}
            </main>

            <footer className="py-6 px-4 md:px-12 text-sm text-gray-400 flex flex-col md:flex-row justify-between items-center relative z-10">
                <div className="flex flex-col md:flex-row items-center gap-2">
                    <a href="https://www.linkedin.com/company/samsara-capital-partners" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-accent">
                        <FontAwesomeIcon icon={faLinkedin} size="lg" />
                    </a>
                    <div>Samsara Capital Partners is the Private Equity Division of <a href="https://pshg.co" target="_blank" rel="noopener noreferrer" className="text-accent-light-400 hover:text-accent-light">Polymath Solutions Holding Group</a> © {new Date().getFullYear()} All Rights Reserved.</div>
                    <div className="flex items-center gap-4 mt-2 md:mt-0 md:ml-4">
                        <a href="https://www.linkedin.com/company/pshg" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-accent">
                            <FontAwesomeIcon icon={faLinkedin} size="lg" />
                        </a>
                    </div>
                </div>
                <div className="mt-4 md:mt-0">
                    The materials on this website are for illustration and discussion purposes only.
                </div>
            </footer>
        </div>
    );
};

export default Layout; 